import { PropsWithChildren } from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives";

import { wacToken } from "@/libraries/design/foundations";
import { AgileHubSideNav } from "@/patterns/agile-overview-sidenav/src";
import { useAgileHubPageTreeData } from "@/templates/agile-hub/src/use-agile-hub-page-tree-data";
import { CallToAction } from "@/templates/agile-hub/types";

export type AgilePageLayoutProps = {
    productDiscussedCta?: CallToAction[] | null;
    relatedTemplateCta?: CallToAction[] | null;
};

const agilePageLayoutCss = css({
    display: "grid",
    gridTemplateColumns: "repeat(12, minmax(0, 1fr))",
    gridTemplateRows: "minmax(0, 1fr) minmax(0, auto)",
    justifyContent: "space-between",
    margin: "auto",
    maxWidth: "100%",
    boxSizing: "border-box",
    paddingInlineStart: "32px",
    paddingInlineEnd: "32px",
    gap: "24px",
    width: "100%",
    paddingBlockStart: "32px",
    paddingBlockEnd: "48px",

    [media.only.sm]: {
        paddingInlineStart: "60px",
        paddingInlineEnd: "60px"
    },
    [media.above.md]: {
        paddingInlineStart: "60px",
        paddingInlineEnd: "60px",
        maxWidth: "1272px",
        margin: "auto",
        gap: "32px"
    }
});

// NOTE: Nested selectors are necessary here to be able to style the dangerouslySetInnerHTML blob
const articleStyles = css({
    "gridColumnStart": "1",
    "gridColumnEnd": "-1",

    [media.above.sm]: {
        gridColumnStart: "4"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "p": {
        color: wacToken("color.secondary.navy"),
        fontSize: "1.125rem",
        lineHeight: "1.5555555"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "h2, h3, h4": {
        fontFamily: `${wacToken("font.charlie.display")} !important`,
        fontWeight: "500",
        color: wacToken("color.extended.blue900")
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "h2": {
        fontSize: "1.5rem",
        lineHeight: "1.3333333",
        [media.above.sm]: {
            fontSize: "2.25rem"
        }
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "h3": {
        fontSize: "1.25rem",
        lineHeight: "1.33333333",
        marginBlockEnd: "16px",
        letterSpacing: ".3px",
        [media.above.sm]: {
            fontSize: "1.5rem"
        }
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    "h4": {
        fontSize: "1.25rem",
        lineHeight: "1.2",
        marginBlockEnd: "16px",
        letterSpacing: ".3px"
    }
});

// Used for Agile Articles, Tutorial, and the Agile Coach pages
// GWP-2776 removed the right rail, view git history to add it back if needed later
export const AgilePageLayout = ({
    children
}: AgilePageLayoutProps & PropsWithChildren) => {
    return (
        <div css={agilePageLayoutCss}>
            <AgileHubSideNav nodes={useAgileHubPageTreeData()} />
            <article className="main-content" css={articleStyles}>
                {children}
            </article>
        </div>
    );
};

export default AgilePageLayout;
